import { gql } from "@apollo/client";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import useUser from "@src/features/app/hooks/useUser";
import { MESSAGING_POLL_INTERVAL_MS } from "@src/features/messaging/new/constants";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import useIntersectionObserver from "@src/lib/hooks/useIntersectionObserver";
import CaretDownFillIcon from "@src/lib/icons/18px/caret-down-fill";
import MsgsOutlineIcon from "@src/lib/icons/18px/msgs-outline";
import PlusIcon from "@src/lib/icons/18px/plus";
import SearchIcon from "@src/lib/icons/18px/search";
import XIcon from "@src/lib/icons/18px/x";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/lib/ui/Collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import Spinner from "@src/lib/ui/Spinner";
import { cn } from "@src/lib/utils/cn";

import { getChatDisplayTitle, isDirectMessage } from "./utils";
import CreateDirectMessageDialog from "./CreateDirectMessageDialog";
import DirectMessageAvatar from "./DirectMessageAvatar";
import EditOrCreateGroupMessageDialog from "./EditOrCreateGroupMessageDialog";
import { useCanCreateChats } from "@src/features/messaging/MessageDialogGate";
import {
  ChatListItemFragment,
  NewMessagingChatSectionsQueryVariables,
  useNewMessagingChatSectionsQuery,
  useNewMessagingSearchChatsQuery,
} from "./ChatsNavSection.generated";
import { useMutedChatIds } from "./useChatNotificationOverride";
import {
  ChatNavSectionKey,
  useChatNavCollapsed,
  useChatNavCollapsedHasHydrated,
} from "./useChatNavCollapsedStore";

export const CHATS_PAGINATION_LIMIT = 50;

type PaginationInput = { limit: number; offset: number };

const useChatSectionPagination = ({
  loadedCount,
  totalCount,
  loading,
  enabled,
  fetchMore,
  makeVariables,
}: {
  loadedCount: number;
  totalCount: number;
  loading: boolean;
  enabled: boolean;
  fetchMore: (options: {
    variables: Partial<NewMessagingChatSectionsQueryVariables>;
  }) => unknown;
  makeVariables: (
    pagination: PaginationInput,
  ) => Partial<NewMessagingChatSectionsQueryVariables>;
}): { loadMoreRef: RefObject<HTMLDivElement | null>; hasMore: boolean } => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const hasMore = loadedCount < totalCount;
  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    fetchMore({
      variables: makeVariables({
        limit: CHATS_PAGINATION_LIMIT,
        offset: loadedCount,
      }),
    });
  }, [loading, hasMore, fetchMore, makeVariables, loadedCount]);
  useIntersectionObserver({
    target: loadMoreRef,
    onIntersect: handleLoadMore,
    threshold: 0.5,
    enabled: hasMore && enabled,
  });
  return { loadMoreRef, hasMore };
};

const unreadVariables = (
  pagination: PaginationInput,
): Partial<NewMessagingChatSectionsQueryVariables> => ({
  unreadPagination: pagination,
});
const groupVariables = (
  pagination: PaginationInput,
): Partial<NewMessagingChatSectionsQueryVariables> => ({
  groupPagination: pagination,
});
const directVariables = (
  pagination: PaginationInput,
): Partial<NewMessagingChatSectionsQueryVariables> => ({
  directPagination: pagination,
});

type ChatsSearchContextValue = {
  value: string;
  setValue: (value: string) => void;
  debouncedValue: string;
};

const ChatsSearchContext = createContext<ChatsSearchContextValue | null>(null);

export const ChatsSearchProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { value, setValue, debouncedValue } = useDebouncedInputValue({
    initialValue: "",
  });
  return (
    <ChatsSearchContext.Provider value={{ value, setValue, debouncedValue }}>
      {children}
    </ChatsSearchContext.Provider>
  );
};

const useChatsSearch = (): ChatsSearchContextValue => {
  const context = useContext(ChatsSearchContext);
  if (!context) {
    throw new Error("useChatsSearch must be used within a ChatsSearchProvider");
  }
  return context;
};

export const ChatsSearchHidden: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { value } = useChatsSearch();
  return value.length > 0 ? null : <>{children}</>;
};

export const ChatsSearchInput: FC = () => {
  const { value: searchValue, setValue: setSearchValue } = useChatsSearch();

  return (
    <div
      className={cn(
        "group/search relative flex h-7 w-full items-center gap-1.5 rounded-lg bg-black-alpha-3 p-2",
        "transition-[background-color,box-shadow] duration-150 ease-out",
        "hover:bg-black-alpha-4",
        "focus-within:bg-background focus-within:shadow-[0_0_0_1px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.06)] focus-within:hover:bg-background",
      )}
    >
      <SearchIcon
        className={cn(
          "pointer-events-none size-3.5 shrink-0",
          "text-gray-9 transition-colors duration-150 ease-out",
          "group-focus-within/search:text-gray-12",
        )}
      />
      <input
        type="text"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Search..."
        aria-label="Search messages"
        className={cn(
          "min-w-0 flex-1 border-none bg-transparent text-gray-12 outline-none",
          "placeholder:text-gray-9",
        )}
      />
      {searchValue.length > 0 && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => setSearchValue("")}
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-md",
            "text-gray-9 transition-colors duration-100 ease-out",
            "hover:bg-black-alpha-3 hover:text-gray-12",
          )}
        >
          <XIcon className="size-3" />
        </button>
      )}
    </div>
  );
};

const ChatsNavSection: FC = () => {
  const { user } = useUser();
  const canCreateChats = useCanCreateChats();
  const collapsedHasHydrated = useChatNavCollapsedHasHydrated();
  const { pathname, query } = useRouter();
  const [newDirectMessageOpen, setNewDirectMessageOpen] = useState(false);
  const [newGroupMessageOpen, setNewGroupMessageOpen] = useState(false);
  const mutedChatIds = useMutedChatIds();

  const { debouncedValue: debouncedSearchValue, setValue: setSearchValue } =
    useChatsSearch();
  const trimmedSearch = debouncedSearchValue.trim();
  const searchActive = trimmedSearch.length > 0;

  const activeChatId =
    pathname === "/messages/my-chats/[chatId]"
      ? (query.chatId as string | undefined)
      : undefined;

  // Poll refetches reuse the widest page already loaded so paginated
  // sections do not collapse back to the first page every interval.
  const loadedUnreadRef = useRef(0);
  const loadedGroupRef = useRef(0);
  const loadedDirectRef = useRef(0);
  const pollPagination = (loaded: number): PaginationInput => ({
    limit: Math.max(CHATS_PAGINATION_LIMIT, loaded),
    offset: 0,
  });

  const { data, error, fetchMore, loading } = useNewMessagingChatSectionsQuery({
    variables: {
      unreadPagination: pollPagination(loadedUnreadRef.current),
      groupPagination: pollPagination(loadedGroupRef.current),
      directPagination: pollPagination(loadedDirectRef.current),
    },
    pollInterval: searchActive ? 0 : MESSAGING_POLL_INTERVAL_MS,
  });

  const unreadChats = data?.MyUnreadMessages.objects || [];
  const groupChats = data?.MyGroupMessages.objects || [];
  const directChats = data?.MyDirectMessages.objects || [];
  const unreadCount = data?.UnreadChatsCount || 0;

  useEffect(() => {
    loadedUnreadRef.current = unreadChats.length;
    loadedGroupRef.current = groupChats.length;
    loadedDirectRef.current = directChats.length;
  }, [unreadChats.length, groupChats.length, directChats.length]);

  const unreadPagination = useChatSectionPagination({
    loadedCount: unreadChats.length,
    totalCount: data?.MyUnreadMessages.totalCount || 0,
    loading,
    enabled: !searchActive,
    fetchMore,
    makeVariables: unreadVariables,
  });
  const groupPagination = useChatSectionPagination({
    loadedCount: groupChats.length,
    totalCount: data?.MyGroupMessages.totalCount || 0,
    loading,
    enabled: !searchActive,
    fetchMore,
    makeVariables: groupVariables,
  });
  const directPagination = useChatSectionPagination({
    loadedCount: directChats.length,
    totalCount: data?.MyDirectMessages.totalCount || 0,
    loading,
    enabled: !searchActive,
    fetchMore,
    makeVariables: directVariables,
  });

  const {
    data: searchData,
    fetchMore: fetchMoreSearchResults,
    loading: searchLoading,
  } = useNewMessagingSearchChatsQuery({
    variables: {
      input: { search: trimmedSearch },
      pagination: { limit: CHATS_PAGINATION_LIMIT, offset: 0 },
    },
    skip: !searchActive,
  });

  const searchResults = searchData?.SearchMyMessages.objects || [];
  const totalSearchResultsCount = searchData?.SearchMyMessages.totalCount || 0;
  const hasMoreSearchResults = searchResults.length < totalSearchResultsCount;

  const searchLoadMoreRef = useRef<HTMLDivElement>(null);
  const handleLoadMoreSearchResults = useCallback(() => {
    if (searchLoading || !hasMoreSearchResults) return;
    fetchMoreSearchResults({
      variables: {
        pagination: {
          limit: CHATS_PAGINATION_LIMIT,
          offset: searchResults.length,
        },
      },
    });
  }, [
    searchLoading,
    hasMoreSearchResults,
    fetchMoreSearchResults,
    searchResults.length,
  ]);
  useIntersectionObserver({
    target: searchLoadMoreRef,
    onIntersect: handleLoadMoreSearchResults,
    threshold: 0.5,
    enabled: hasMoreSearchResults && searchActive,
  });

  const renderChatRow = (
    chat: ChatListItemFragment,
    onNavigate?: () => void,
  ) => (
    <ChatNavItem
      key={chat.id}
      chat={chat}
      viewerId={user?.id}
      muted={mutedChatIds.has(chat.id)}
      active={activeChatId === chat.id}
      onNavigate={onNavigate}
    />
  );

  const renderChat = (chat: ChatListItemFragment) => renderChatRow(chat);

  return (
    <SidebarGroup className="pt-4">
      <SidebarGroupContent>
        {searchActive ? (
          <>
            <SidebarMenu>
              {searchResults.map((chat) =>
                renderChatRow(chat, () => setSearchValue("")),
              )}
            </SidebarMenu>
            {searchLoading && searchResults.length === 0 && (
              <Spinner style={{ margin: "auto" }} />
            )}
            {!searchLoading && totalSearchResultsCount === 0 && (
              <div className="px-2 py-1 text-xs font-medium text-gray-9">
                No results
              </div>
            )}
            {hasMoreSearchResults && (
              <div ref={searchLoadMoreRef} className="mt-2 self-stretch">
                <Spinner style={{ margin: "auto" }} />
              </div>
            )}
          </>
        ) : (!data && !error) || !collapsedHasHydrated ? (
          <div className="py-6">
            <Spinner style={{ margin: "auto" }} />
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {unreadChats.length > 0 && (
              <ChatNavSectionGroup
                section="unread"
                label="Unread"
                badgeCount={unreadCount}
                chats={unreadChats}
                activeChatId={activeChatId}
                renderChat={renderChat}
                hasMore={unreadPagination.hasMore}
                loadMoreRef={unreadPagination.loadMoreRef}
              />
            )}
            <ChatNavSectionGroup
              section="group"
              label="Group Messages"
              chats={groupChats}
              activeChatId={activeChatId}
              renderChat={renderChat}
              hasMore={groupPagination.hasMore}
              loadMoreRef={groupPagination.loadMoreRef}
              createLabel={canCreateChats ? "New group message" : undefined}
              onCreate={() => setNewGroupMessageOpen(true)}
            />
            <ChatNavSectionGroup
              section="direct"
              label="Direct Messages"
              chats={directChats}
              activeChatId={activeChatId}
              renderChat={renderChat}
              hasMore={directPagination.hasMore}
              loadMoreRef={directPagination.loadMoreRef}
              createLabel={canCreateChats ? "New direct message" : undefined}
              onCreate={() => setNewDirectMessageOpen(true)}
            />
          </div>
        )}
      </SidebarGroupContent>
      {newDirectMessageOpen && (
        <CreateDirectMessageDialog
          open={newDirectMessageOpen}
          onOpenChange={setNewDirectMessageOpen}
        />
      )}
      {newGroupMessageOpen && (
        <EditOrCreateGroupMessageDialog
          open={newGroupMessageOpen}
          onOpenChange={setNewGroupMessageOpen}
        />
      )}
    </SidebarGroup>
  );
};

const ChatNavSectionGroup: FC<{
  section: ChatNavSectionKey;
  label: string;
  chats: ChatListItemFragment[];
  activeChatId: string | undefined;
  renderChat: (chat: ChatListItemFragment) => ReactNode;
  hasMore: boolean;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  badgeCount?: number;
  createLabel?: string;
  onCreate?: () => void;
}> = ({
  section,
  label,
  chats,
  activeChatId,
  renderChat,
  hasMore,
  loadMoreRef,
  badgeCount,
  createLabel,
  onCreate,
}) => {
  const { collapsed, setCollapsed } = useChatNavCollapsed(section);
  const activeChat = chats.find((chat) => chat.id === activeChatId);

  return (
    <Collapsible open={!collapsed} onOpenChange={(open) => setCollapsed(!open)}>
      <div className="group/section flex h-7 w-full items-center gap-1.5 rounded-lg p-2 text-gray-11 hover:bg-black-alpha-2">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          >
            <span className="relative flex size-[18px] shrink-0 items-center justify-center">
              <CaretDownFillIcon
                className={cn(
                  "size-2.5 shrink-0 text-gray-12 transition-transform duration-100 ease-out",
                  collapsed && "-rotate-90",
                )}
              />
            </span>
            <span className="min-w-0 flex-1 truncate">{label}</span>
          </button>
        </CollapsibleTrigger>
        {((collapsed && badgeCount != null && badgeCount > 0) ||
          createLabel) && (
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {collapsed && badgeCount != null && badgeCount > 0 && (
              <ChatUnreadBadge count={badgeCount} />
            )}
            {createLabel && (
              <button
                type="button"
                aria-label={createLabel}
                onClick={onCreate}
                className="shrink-0 text-gray-11 opacity-0 group-hover/section:opacity-100 hover:text-gray-12 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
              >
                <PlusIcon className="size-3" />
              </button>
            )}
          </span>
        )}
      </div>
      {collapsed && activeChat && (
        <SidebarMenu className="mt-1">{renderChat(activeChat)}</SidebarMenu>
      )}
      <CollapsibleContent>
        <SidebarMenu className="mt-1">{chats.map(renderChat)}</SidebarMenu>
        {hasMore && (
          <div ref={loadMoreRef} className="mt-2 self-stretch">
            <Spinner style={{ margin: "auto" }} />
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const ChatNavItem: FC<{
  chat: ChatListItemFragment;
  viewerId: number | undefined;
  muted: boolean;
  active: boolean;
  onNavigate?: () => void;
}> = ({ chat, viewerId, muted, active, onNavigate }) => {
  const directMessage = isDirectMessage(chat);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        aria-current={active ? "page" : undefined}
        className={cn(muted && "text-gray-8")}
      >
        <Link
          href={{
            pathname: "/messages/my-chats/[chatId]",
            query: { chatId: chat.id },
          }}
          onClick={onNavigate}
        >
          {directMessage ? (
            <DirectMessageAvatar
              members={chat.members}
              viewerId={viewerId}
              size={18}
            />
          ) : (
            <span className="flex size-[18px] shrink-0 items-center justify-center">
              <MsgsOutlineIcon className="size-4" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">
            {getChatDisplayTitle(chat)}
          </span>
          {chat.unreadCount > 0 && <ChatUnreadBadge count={chat.unreadCount} />}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const ChatUnreadBadge: FC<{ count: number }> = ({ count }) => (
  <span className="flex h-[15px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-brand px-1 text-[10px] leading-none font-semibold tracking-[-0.5px] text-white">
    {count}
  </span>
);

gql`
  query NewMessagingChatSections(
    $unreadPagination: PaginationInput
    $groupPagination: PaginationInput
    $directPagination: PaginationInput
  ) {
    UnreadChatsCount
    MyUnreadMessages(pagination: $unreadPagination) {
      totalCount
      objects {
        ...ChatListItem
      }
    }
    MyGroupMessages(pagination: $groupPagination) {
      totalCount
      objects {
        ...ChatListItem
      }
    }
    MyDirectMessages(pagination: $directPagination) {
      totalCount
      objects {
        ...ChatListItem
      }
    }
  }

  query NewMessagingSearchChats(
    $input: SearchMyMessagesInput!
    $pagination: PaginationInput
  ) {
    SearchMyMessages(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...ChatListItem
      }
    }
  }

  fragment ChatListItem on Chat {
    id
    chatType
    viewerIsAdminOrChatOwner
    membersCanEditTitle
    membersCanEditMembers
    unreadCount
    title {
      en
    }
    displayTitle
    userSelectionSet {
      id
      userSelections {
        id
        userMemberships {
          id
        }
      }
    }
    viewerArchivedAt
    latestMessageText
    latestMessageHasAttachments
    members {
      id
      name
      firstName
      deactivatedAt
      profileImage {
        ...AvatarProfileImage
      }
    }
  }
`;

export default ChatsNavSection;
